import React, { useEffect, useState } from "react";
import { CheckCircle2, CircleDashed, Coffee, MapPinned, PartyPopper, Plane, XCircle, Clock3 } from "lucide-react";
import { Badge } from "../ui/badge";
import { api, errMsg } from "../../lib/api";

/** Phase 168 — attendance status meta (labels + theme-token tones; never colour alone, always text). */
export const STATUS_META = {
  present: { label: "Present", cls: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20", icon: CheckCircle2, dot: "bg-[hsl(var(--success))]" },
  field_visit: { label: "Field Visit", cls: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20", icon: MapPinned, dot: "bg-[hsl(var(--info))]" },
  half_day: { label: "Half Day", cls: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20", icon: CircleDashed, dot: "bg-[hsl(var(--warning))]" },
  absent: { label: "Absent", cls: "bg-destructive/10 text-destructive border-destructive/20", icon: XCircle, dot: "bg-destructive" },
  leave: { label: "Leave", cls: "bg-violet-100 text-violet-800 border-violet-200", icon: Plane, dot: "bg-violet-500" },
  holiday: { label: "Holiday", cls: "bg-muted text-muted-foreground border-border", icon: PartyPopper, dot: "bg-muted-foreground/60" },
  weekly_off: { label: "Weekly Off", cls: "bg-muted text-muted-foreground border-border", icon: Coffee, dot: "bg-muted-foreground/40" },
  pending: { label: "Pending", cls: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20", icon: Clock3, dot: "bg-[hsl(var(--warning))]" },
};
export const STATUS_ORDER = ["present", "field_visit", "half_day", "absent", "leave", "holiday", "weekly_off"];
export const statusLabel = (s) => STATUS_META[s]?.label || s || "—";

export function StatusBadge({ status, className = "", testId, size = "sm" }) {
  const m = STATUS_META[status] || { label: status || "—", cls: "bg-muted text-muted-foreground border-border", icon: CircleDashed };
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`${size === "xs" ? "text-[10px] px-1.5 py-0" : "text-[11px]"} font-medium whitespace-nowrap ${m.cls} ${className}`}
      data-testid={testId} data-status={status}>
      <Icon className="h-3 w-3 mr-1 shrink-0" aria-hidden="true" /> {m.label}
    </Badge>
  );
}

export const LEAVE_STATUS_CLS = {
  pending: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20",
  approved: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
  cancelled: "bg-muted text-muted-foreground border-border",
};
export function LeaveStatusBadge({ status, testId }) {
  return <Badge variant="outline" className={`text-[11px] capitalize ${LEAVE_STATUS_CLS[status] || ""}`} data-testid={testId} data-status={status}>{status}</Badge>;
}

export const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
export const n = (v) => Number(v || 0).toLocaleString("en-IN");
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};
export const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
export const daysInMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m, 0).getDate();
};
export const dayISO = (ym, d) => `${ym}-${String(d).padStart(2, "0")}`;
/** 0 = Monday … 6 = Sunday for a YYYY-MM-DD string (local calendar maths, no timezone drift). */
export const weekdayOf = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return (new Date(y, m - 1, d).getDay() + 6) % 7;
};

/** Filter dropdown options for the admin page (zones, wards, collectors, supervisors). */
export function useAttendanceOptions(code) {
  const [opts, setOpts] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/t/${code}/attendance/options`)
      .then((r) => { if (alive) setOpts(r.data); })
      .catch((e) => { if (alive) setError(errMsg(e)); });
    return () => { alive = false; };
  }, [code]);
  return { opts, error };
}

/** Attendance rules (thresholds etc.) for any staff role. */
export function useAttendanceRules(code) {
  const [rules, setRules] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/t/${code}/attendance/rules`).then((r) => { if (alive) setRules(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [code]);
  return rules;
}

export const RULE_FIELDS = [
  { key: "visit_threshold", label: "Visits required per day", type: "number", min: 0, hint: "Present when the collector completes at least this many property visits in the day." },
  { key: "collection_threshold", label: "Collection required per day (₹)", type: "number", min: 0, hint: "…or collects at least this amount — either condition is enough." },
  { key: "late_cutoff_time", label: "Late sign-in cutoff (IST)", type: "time", hint: "Leave empty to follow the municipality's duty start time." },
  { key: "late_grace_minutes", label: "Grace minutes", type: "number", min: 0, max: 240, hint: "Sign-ins up to this many minutes after the cutoff are not late." },
  { key: "late_login_limit", label: "Late sign-ins tolerated per month", type: "number", min: 0, max: 31, hint: "From the next late sign-in onwards the day counts as Half Day (when the requirement was met)." },
  { key: "late_after_limit_half_day", label: "Half Day after the late limit", type: "switch", hint: "Turn off to only record late sign-ins without the Half Day penalty." },
  { key: "partial_half_day_enabled", label: "Half Day on partial requirement", type: "switch", hint: "When the requirement is not met but a share of it is, record Half Day instead of Absent." },
  { key: "partial_half_day_pct", label: "Partial requirement share (%)", type: "number", min: 1, max: 99, hint: "Minimum share of either requirement that earns a Half Day." },
  { key: "finalise_time", label: "Finalise previous day at (IST)", type: "time", hint: "The nightly run marks the day final and notifies Half Day / Absent collectors after this time." },
  { key: "sunday_weekly_off", label: "Sundays are weekly offs", type: "switch", hint: "Admins can still declare an individual Sunday as a working day with a reason." },
  { key: "notify_half_day_absent", label: "Notify Half Day / Absent in-app", type: "switch", hint: "Collectors get an in-app notice with the date, status and reason (no SMS)." },
];
